import React, { useState, ChangeEvent } from 'react';
import { FormControl, FormControlLabel, Checkbox, Radio, TextField, Button, Box } from '@mui/material';
import { AssessmentQuestion, AssessmentQuestionType, Choice, MultipleChoiceQuestion, SingleChoiceQuestion, TextInputQuestion, FileUploadQuestion } from '../../utils/assessmentsQuestions';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';

interface QuestionProps {
    question: AssessmentQuestion;
    onChange: (updatedQuestion: AssessmentQuestion) => void;
    //isEditMode: boolean;
    descriptionFieldEmpty: boolean;
    choiceFieldsEmpty: boolean;
}

const Question: React.FC<QuestionProps> = ({ question, onChange, descriptionFieldEmpty, choiceFieldsEmpty }) => {

    const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange({ ...question, description: event.target.value });

    };

    const handleChoiceChange = (choice_id: number, text: string) => {
        if (question.type === AssessmentQuestionType.MultipleChoice || question.type === AssessmentQuestionType.SingleChoice) {
            const updatedChoices = (question as MultipleChoiceQuestion | SingleChoiceQuestion).choices.map((choice) =>
                choice.choice_id === choice_id ? { ...choice, text } : choice
            );
            onChange({ ...question, choices: updatedChoices });
        }
    };

    const handleAddChoice = () => {
        if (question.type === AssessmentQuestionType.MultipleChoice || question.type === AssessmentQuestionType.SingleChoice) {
            const newChoiceId = Math.max(0, ...(question as MultipleChoiceQuestion | SingleChoiceQuestion).choices.map(c => c.choice_id)) + 1;
            const newChoice: Choice = { choice_id: newChoiceId, text: '' };
            onChange({
                ...question,
                choices: [...(question as MultipleChoiceQuestion | SingleChoiceQuestion).choices, newChoice]
            });
        }
    };

    const handleDeleteChoice = (choice_id: number) => {
        if (question.type === AssessmentQuestionType.MultipleChoice || question.type === AssessmentQuestionType.SingleChoice) {
            const updatedChoices = (question as MultipleChoiceQuestion | SingleChoiceQuestion).choices.filter(choice => choice.choice_id !== choice_id);
            onChange({ ...question, choices: updatedChoices });
        }
    };

    const handleCorrectAnswerChange = (choice_id: number, checked: boolean) => {
        if (question.type === AssessmentQuestionType.MultipleChoice) {
            const updatedChoices = (question as MultipleChoiceQuestion).choices.map((choice) =>
                choice.choice_id === choice_id ? { ...choice, isCorrect: checked } : choice
            );
            onChange({ ...question, choices: updatedChoices });
        } else if (question.type === AssessmentQuestionType.SingleChoice) {
            // For single choice, only one can be correct
            const updatedChoices = (question as SingleChoiceQuestion).choices.map((choice) =>
                choice.choice_id === choice_id ? { ...choice, isCorrect: true } : { ...choice, isCorrect: false }
            );
            onChange({ ...question, choices: updatedChoices });
        }
    };

    const handlePointsChange = (event: ChangeEvent<HTMLInputElement>) => {
        onChange({ ...question, points: parseInt(event.target.value) || 0 });
    };

    return (
        <Box sx={{ marginBottom: '1rem' }}>
            <FormControl fullWidth margin="normal">
                <TextField
                    label="Question Description"
                    required
                    variant="outlined"
                    value={question.description}
                    onChange={handleDescriptionChange}
                    fullWidth
                    margin="normal"
                    error={descriptionFieldEmpty}
                />
                {question.type === AssessmentQuestionType.MultipleChoice && (

                    <>
                        <TextField
                            label="Maximum Grade"
                            required
                            variant="outlined"
                            type="number"
                            value={question.points}
                            onChange={handlePointsChange}
                            fullWidth
                            margin="normal"
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                        {[...(question as MultipleChoiceQuestion).choices]
                            .sort((a, b) => a.choice_id - b.choice_id)
                            .map((choice, index) => (
                            <Box key={choice.choice_id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>

                                <FormControlLabel
                                    key={choice.choice_id}
                                    control={
                                        <Checkbox
                                            checked={choice.isCorrect || false}
                                            onChange={(e) => handleCorrectAnswerChange(choice.choice_id, e.target.checked)}
                                        />
                                    }
                                    label={
                                        <TextField
                                            label={`Option ${index + 1}`}
                                            variant="outlined"
                                            required
                                            value={choice.text}
                                            onChange={(e) => handleChoiceChange(choice.choice_id, e.target.value)}
                                            fullWidth
                                            margin="normal"
                                            error={choiceFieldsEmpty}
                                        />
                                    }
                                />

                                    <IconButton onClick={() => handleDeleteChoice(choice.choice_id)} sx={{ color: 'red' }}>
                                        <DeleteIcon />
                                    </IconButton>
                            </Box>
                        ))}



                        <Box sx={{ display: 'flex' }}>
                            <IconButton onClick={handleAddChoice} sx={{ color: "var(--ttg-black)" }}>
                                <AddCircleIcon />
                            </IconButton>
                        </Box>

                    </>
                )}
                {question.type === AssessmentQuestionType.SingleChoice && (

                    <>
                        <TextField
                            label="Maximum Grade"
                            required
                            variant="outlined"
                            type="number"
                            value={question.points}
                            onChange={handlePointsChange}
                            fullWidth
                            margin="normal"
                            InputProps={{ inputProps: { min: 0 } }}
                        />
                        {[...(question as SingleChoiceQuestion).choices]
                            .sort((a, b) => a.choice_id - b.choice_id)
                            .map((choice, index) => (
                            <Box key={choice.choice_id} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>

                                <FormControlLabel
                                    key={choice.choice_id}
                                    control={
                                        <Radio
                                            checked={choice.isCorrect || false}
                                            onChange={(e) => handleCorrectAnswerChange(choice.choice_id, e.target.checked)}
                                        />
                                    }
                                    label={
                                        <TextField
                                            label={`Option ${index + 1}`}
                                            variant="outlined"
                                            required
                                            value={choice.text}
                                            onChange={(e) => handleChoiceChange(choice.choice_id, e.target.value)}
                                            fullWidth
                                            margin="normal"
                                            error={choiceFieldsEmpty}
                                        />
                                    }
                                />

                                    <IconButton onClick={() => handleDeleteChoice(choice.choice_id)} sx={{ color: 'red' }}>
                                        <DeleteIcon />
                                    </IconButton>
                            </Box>
                        ))}



                        <Box sx={{ display: 'flex' }}>
                            <IconButton onClick={handleAddChoice} sx={{ color: "var(--ttg-black)" }}>
                                <AddCircleIcon />
                            </IconButton>
                        </Box>

                    </>
                )}
                {question.type === AssessmentQuestionType.TextInput && (
                    <TextField
                        label="Maximum Grade"
                        required
                        variant="outlined"
                        type="number"
                        value={question.points}
                        onChange={handlePointsChange}
                        fullWidth
                        margin="normal"
                        InputProps={{ inputProps: { min: 0 } }}
                    />
                )}
                {question.type === AssessmentQuestionType.FileUpload && (

                    <TextField
                        label="Maximum Grade"
                        required
                        variant="outlined"
                        type="number"
                        value={question.points}
                        onChange={handlePointsChange}
                        fullWidth
                        margin="normal"
                        InputProps={{ inputProps: { min: 0 } }}
                    />
                )}
            </FormControl>
        </Box>
    );
};

export default Question;
